import { format } from 'date-fns';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import 'react-accessible-accordion/dist/fancy-example.css';
import { Box, Chip, LinearProgress, MenuItem, Select, TextField, Typography, Button, Card, CardContent } from '@mui/material';
import { Accordion, AccordionItem, AccordionItemHeading, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion';
import { Visibility as ViewIcon, Undo as RollbackIcon, CheckCircle as SuccessIcon, Error as ErrorIcon, Schedule as PendingIcon, Cancel as CancelIcon, Download as DownloadIcon } from '@mui/icons-material';

import ImportWizard from '../../../components/ImportWizard';
import { getImportHistory, getImportStatus, rollbackImport } from '../../../store/Objects';
import TableAnimationFiles from '../../../components/lottiefilesAnimation/TableAnimationFiles.js';

const ImportHistory = () => {
  const dispatch = useDispatch();

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedTransferType, setSelectedTransferType] = useState('all');

  // Get import history for all modules when 'all' is selected
  const contactsData = useSelector(state => state['Objects']?.['contacts']);
  const companiesData = useSelector(state => state['Objects']?.['companies']);
  const dealsData = useSelector(state => state['Objects']?.['deals']);

  // Combine import history from all modules when 'all' is selected
  const importHistory = React.useMemo(() => {
    if (selectedModule === 'all') {
      const allJobs = [];
      if (contactsData?.['importHistory']?.['jobs']) allJobs.push(...contactsData['importHistory']['jobs']);
      if (companiesData?.['importHistory']?.['jobs']) allJobs.push(...companiesData['importHistory']['jobs']);
      if (dealsData?.['importHistory']?.['jobs']) allJobs.push(...dealsData['importHistory']['jobs']);
      return { jobs: allJobs, total: allJobs.length };
    } else {
      const objectData = selectedModule === 'contacts' ? contactsData :
        selectedModule === 'companies' ? companiesData :
          selectedModule === 'deals' ? dealsData : null;
      return objectData?.['importHistory'] || { jobs: [], total: 0 };
    }
  }, [selectedModule, contactsData, companiesData, dealsData]);

  const isLoading = contactsData?.['loading'] || companiesData?.['loading'] || dealsData?.['loading'] || false;
  const [selectedStatus, setSelectedStatus] = useState('completed');

  const [searchTerm, setSearchTerm] = useState('');
  const [pollingJobs, setPollingJobs] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [activeSubFilter, setActiveSubFilter] = useState(null);
  const handleFilterClick = (filter) => setActiveSubFilter(filter);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState({ open: false, job: null });
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [detailedJobData, setDetailedJobData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch detailed results when dialog opens
  useEffect(() => {
    if (detailsDialog.open && detailsDialog.job?.jobId) {
      setLoadingDetails(true);
      dispatch(getImportStatus({
        jobId: detailsDialog.job.jobId,
        objectType: detailsDialog.job.objectType,
        successCallback: (data) => {
          setDetailedJobData(data);
          setLoadingDetails(false);
        },
        failCallback: () => {
          setLoadingDetails(false);
        }
      }));
    }
  }, [detailsDialog.open, detailsDialog.job?.jobId, dispatch]);

  const modules = [
    { value: 'all', label: 'All Modules' },
    { value: 'contacts', label: 'People' },
    { value: 'companies', label: 'Accounts' },
    { value: 'deals', label: 'Pipeline' }
  ];

  const transferTypes = [
    { value: 'all', label: 'Import & Export' },
    { value: 'import', label: 'Import Only' },
    { value: 'export', label: 'Export Only' }
  ];

  const statuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'processing', label: 'Processing' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'pending', label: 'Pending' },
    { value: 'rolled_back', label: 'Rolled Back' }
  ];

  useEffect(() => { fetchImportHistory() }, [selectedModule]);

  useEffect(() => {
    if (importHistory['jobs']) {
      // Poll for in-progress imports
      const inProgressJobs = importHistory['jobs'].filter(job => job['status'] === 'processing' || job['status'] === 'pending');
      setPollingJobs(inProgressJobs);

      // if (inProgressJobs.length > 0) {
      //   const interval = setInterval(() => {
      //     inProgressJobs.forEach(job => dispatch(getImportStatus({
      //       jobId: job['id'],
      //       objectType: job['objectType'] || selectedModule || 'contacts',
      //     })))
      //   }, 3000);
      //   return () => clearInterval(interval);
      // }
    }
  }, [importHistory]);

  const fetchImportHistory = () => {
    if (selectedModule === 'all') {
      // Fetch all import/export history for all modules - filtering will be done on frontend
      dispatch(getImportHistory({ objectType: 'contacts', transferType: 'all' }));
      dispatch(getImportHistory({ objectType: 'companies', transferType: 'all' }));
      dispatch(getImportHistory({ objectType: 'deals', transferType: 'all' }));
    } else dispatch(getImportHistory({ objectType: selectedModule, transferType: 'all' }));
  }

  const handleRollback = async (job) => {
    if (window.confirm('Are you sure you want to rollback this import? This will remove all records created by this import.')) {
      dispatch(rollbackImport({
        jobId: job.jobId,
        objectType: job.objectType,
        successCallback: () => {
          toast.success('Import rolled back successfully');
          setDetailsDialog({ open: false, job: null });
          setDetailedJobData(null);
          fetchImportHistory();
        },
        failCallback: (error) => { toast.error('Failed to rollback import: ' + (error?.response?.data?.message || 'Unknown error')) }
      }))
    }
  }

  const handleDownloadExport = async (job) => {
    // For completed exports, we need to get the download URL
    if (job.status === 'completed' && job.downloadAvailable) {
      // Fetch the detailed job data to get the download URL
      dispatch(getImportStatus({
        jobId: job.jobId,
        objectType: job.objectType,
        successCallback: (data) => {
          if (data?.results?.downloadUrl)
            window.open(data.results.downloadUrl, '_blank');
          else toast.error('Download URL not available');
        },
        failCallback: () => { toast.error('Failed to get download URL') }
      }));
    } else toast.error('Export file is not available for download');
  }

  const handleViewDetails = (job) => setDetailsDialog({ open: true, job });

  const handleExpandRow = (jobId) => setExpandedRow(expandedRow === jobId ? null : jobId);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'processing':
        return <CircularProgress size={16} />;
      case 'cancelled':
        return <CancelIcon color="action" />;
      case 'pending':
        return <PendingIcon color="action" />;
      case 'rolled_back':
        return <RollbackIcon color="action" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'info';
      case 'cancelled':
        return 'default';
      case 'pending':
        return 'warning';
      case 'rolled_back':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredHistory = (importHistory?.['jobs'] || []).filter(job => {
    // Filter by transfer type
    if (selectedTransferType !== 'all' && job.transferType !== selectedTransferType) return false;
    
    // Filter by status
    if (selectedStatus !== 'all' && job.status !== selectedStatus) return false;
    
    // Filter by search term - check both fileName and original name
    if (searchTerm) {
      const fileName = job.fileName || job['fileInfo']?.['originalName'] || '';
      if (!fileName.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    }
    
    return true;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const handleModuleChange = (event) => {
    setSelectedModule(event['target']['value']);
  };

  const [colWidths, setColWidths] = useState({
    fileInfo: 300,
    objectType: 120,
    status: 150,
    progress: 120,
    createdAt: 150,
    createdAt: 190,
  });

  const setColWidth = (key, value) => {
    setColWidths(prev => ({ ...prev, [key]: value }));
  };

  const columns = [
    {
      name: <ResizableHeader title="File Name" width={colWidths['fileInfo']} onResize={w => setColWidth('fileInfo', w)} />,
      width: `${colWidths['fileInfo']}px`,
      selector: row => row.fileName || row['fileInfo']?.['originalName'],
      cell: (row) => {
        const fileName = row.fileName || row['fileInfo']?.['originalName'];
        const fileSize = row.fileSize || row['fileInfo']?.['size'];
        return (
          <div className="content-area" onClick={e => e.stopPropagation()}>
            <Typography variant="body2">
              {fileName}
              {fileSize && <span className='file-size'>({formatFileSize(fileSize)})</span>}
            </Typography>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button className='create-btn dropdwon-email view-btn-import' onClick={() => handleViewDetails(row)}>
                <ViewIcon />
              </button>
              {row.transferType === 'export' && row.status === 'completed' && row.downloadAvailable && (
                <button 
                  className='create-btn dropdwon-email view-btn-import' 
                  onClick={() => handleDownloadExport(row)}
                  title="Download Export"
                >
                  <DownloadIcon />
                </button>
              )}
            </div>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: <ResizableHeader title="Module" width={colWidths['objectType']} onResize={w => setColWidth('objectType', w)} />,
      width: `${colWidths['objectType']}px`,
      selector: row => row['objectType'],
      cell: row => <Chip label={row['objectType']} size="small" variant="outlined" />,
      sortable: true,
    },
    {
      name: <ResizableHeader title="Type" width={colWidths['transferType'] || 100} onResize={w => setColWidth('transferType', w)} />,
      width: `${colWidths['transferType'] || 100}px`,
      selector: row => row.transferType,
      cell: row => (
        <Chip
          label={row.transferType}
          size="small"
          variant="outlined"
          sx={{
            color: row.transferType === 'import' ? '#60a5fa' : '#c43a7a',
            borderColor: row.transferType === 'import' ? '#60a5fa' : '#c43a7a',
            backgroundColor: row.transferType === 'import' ? 'rgba(96,165,250,0.1)' : 'rgba(196,58,122,0.1)',
          }}
        />
      ),
      sortable: true,
    },
    {
      name: <ResizableHeader title="Status" width={colWidths['status']} onResize={w => setColWidth('status', w)} />,
      width: `${colWidths['status']}px`,
      selector: row => row['status'],
      cell: row => (
        <Chip
          size="small"
          label={row['status'].replace(/_/g, ' ')}
          icon={getStatusIcon(row['status'])}
          color={getStatusColor(row['status'])}
        />
      ),
      sortable: true,
    },
    {
      name: <ResizableHeader title="Records" width={colWidths['progress']} onResize={w => setColWidth('progress', w)} />,
      width: `${colWidths['progress']}px`,
      cell: row => {
        const processed = row.recordsProcessed || 0;
        const total = row.totalRecords || 0;
        const progress = row['progress'] || {};
        return (
          <Box>
            <Typography variant="body2">
              {processed} / {total}
            </Typography>
            {row['status'] === 'processing' && (
              <LinearProgress variant="determinate" value={progress['percentComplete'] || 0} sx={{ mt: 0.5 }} />
            )}
          </Box>
        );
      },
    },
    {
      name: <ResizableHeader title="Date" width={colWidths['createdAt']} onResize={w => setColWidth('createdAt', w)} />,
      width: `${colWidths['createdAt']}px`,
      selector: row => row['createdAt'],
      cell: row => (
        <>
          {format(new Date(row['createdAt']), 'PP')}
          <Typography variant="caption" display="block" color="text.secondary">
            {format(new Date(row['createdAt']), 'p')}
          </Typography>
        </>
      ),
      sortable: true,
    }
  ]

  const CustomSortIcon = () => (
    <span className="custom-sort-icon">
      <span className="up">▲</span>
      <span className="down">▼</span>
    </span>
  );

  const renderDetailsDialog = () => {
    const job = detailsDialog.job;
    if (!job) return null;

    const downloadFailedRecords = () => {
      if (!detailedJobData?.results?.failed || detailedJobData.results.failed.length === 0) {
        toast.error('No failed records to download');
        return;
      }

      // Create CSV content
      const failedRecords = detailedJobData.results.failed;
      const headers = ['Row', 'Error Messages', ...Object.keys(failedRecords[0]?.data || {})];

      const csvContent = [
        headers.join(','),
        ...failedRecords.map(record => {
          const row = [
            record.row,
            `"${(record.errorMessages || []).join('; ')}"`,
            ...Object.values(record.data || {}).map(val => `"${val || ''}"`),
          ];
          return row.join(',');
        })
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `failed_records_${job.jobId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    };

    return (
      <Modal isOpen={detailsDialog.open} onClose={() => { setDetailsDialog({ open: false, job: null }); setDetailedJobData(null); }} className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal import--export-details-modal`}>
        <ModalHeader toggle={() => { setDetailsDialog({ open: false, job: null }); setDetailedJobData(null); }}>
          <h3>{job?.transferType === 'export' ? 'Export' : 'Import'} Details - {job?.fileName || job?.['fileInfo']?.['originalName']}</h3>
        </ModalHeader>
        <ModalBody>
          <div className='credits-area workspace-modal mt-3'>
            {loadingDetails ? (
              <div className='loading-container' style={{ padding: '50px', textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>Loading detailed results...</Typography>
              </div>
            ) : (
              <div className='row'>
                <div className='col-lg-6 col-md-12'>
                  <div className='group-form'>
                    <h4>Job ID </h4>
                    <label>{job.jobId}</label>
                  </div>
                </div>
                <div className='col-lg-6 col-md-12'>
                  <div className='group-form'>
                    <h4>Module</h4>
                    <label>{job.objectType}</label>
                  </div>
                </div>
                <div className='col-lg-6 col-md-12'>
                  <div className='group-form'>
                    <h4>Status</h4>
                    <Chip
                      size="small"
                      label={job['status']}
                      icon={getStatusIcon(job['status'])}
                      color={getStatusColor(job['status'])}
                    />
                  </div>
                </div>
                <div className='col-12'>
                  <h4>Progress Summary</h4>
                </div>
                
                {/* Different summary boxes for import vs export */}
                {detailedJobData?.transferType === 'export' || job?.transferType === 'export' ? (
                  <>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box primary'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10px" height="10px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.5" d="M3 10C3 6.22876 3 4.34315 4.17157 3.17157C5.34315 2 7.22876 2 11 2H13C16.7712 2 18.6569 2 19.8284 3.17157C21 4.34315 21 6.22876 21 10V14C21 17.7712 21 19.6569 19.8284 20.8284C18.6569 22 16.7712 22 13 22H11C7.22876 22 5.34315 22 4.17157 20.8284C3 19.6569 3 17.7712 3 14V10Z" stroke="#a33b5c" stroke-width="1.5" />
                              <path d="M12 6L12 8M12 8L12 10M12 8H9.99998M12 8L14 8" stroke="#a33b5c" stroke-width="1.5" stroke-linecap="round" />
                              <path d="M8 14H16" stroke="#a33b5c" stroke-width="1.5" stroke-linecap="round" />
                              <path d="M9 18H15" stroke="#a33b5c" stroke-width="1.5" stroke-linecap="round" />
                            </svg>
                          </i>
                          {job.totalRecords || job.progress?.totalRecords || 0}
                        </h4>
                        <p>Total Records</p>
                      </div>
                    </div>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box success-main'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 10C2.24186 10 0 7.75814 0 5C0 2.24186 2.24186 0 5 0C7.75814 0 10 2.24186 10 5C10 7.75814 7.75814 10 5 10ZM5 0.697674C2.62791 0.697674 0.697674 2.62791 0.697674 5C0.697674 7.37209 2.62791 9.30233 5 9.30233C7.37209 9.30233 9.30233 7.37209 9.30233 5C9.30233 2.62791 7.37209 0.697674 5 0.697674Z" fill="#16A34A"></path><path d="M4.23915 6.76841C4.14613 6.76841 4.05775 6.7312 3.99264 6.66609L2.67636 5.34981C2.54147 5.21492 2.54147 4.99167 2.67636 4.85679C2.81124 4.7219 3.0345 4.7219 3.16938 4.85679L4.23915 5.92655L6.62985 3.53586C6.76473 3.40097 6.98799 3.40097 7.12287 3.53586C7.25775 3.67074 7.25775 3.89399 7.12287 4.02888L4.48566 6.66609C4.42054 6.7312 4.33217 6.76841 4.23915 6.76841Z" fill="#16A34A"></path></svg>
                          </i>
                          {detailedJobData?.results?.recordsExported || job.recordsProcessed || 0}
                        </h4>
                        <p>Exported</p>
                      </div>
                    </div>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10px" height="10px" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="#888"/>
                            </svg>
                          </i>
                          {formatFileSize(detailedJobData?.results?.fileSize || job.fileSize)}
                        </h4>
                        <p>File Size</p>
                      </div>
                    </div>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10px" height="10px" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M17 7h-4V3c0-1.11-.9-2-2-2H9C7.9 1 7 1.9 7 3v4H3c-1.1 0-2 .9-2 2s.9 2 2 2h1v7c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-7h1c1.1 0 2-.9 2-2s-.9-2-2-2zM9 3h2v4H9V3zm5 13H6v-5h8v5z" fill="#888"/>
                            </svg>
                          </i>
                          {job.format?.toUpperCase() || 'CSV'}
                        </h4>
                        <p>Format</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box primary'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10px" height="10px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.5" d="M3 10C3 6.22876 3 4.34315 4.17157 3.17157C5.34315 2 7.22876 2 11 2H13C16.7712 2 18.6569 2 19.8284 3.17157C21 4.34315 21 6.22876 21 10V14C21 17.7712 21 19.6569 19.8284 20.8284C18.6569 22 16.7712 22 13 22H11C7.22876 22 5.34315 22 4.17157 20.8284C3 19.6569 3 17.7712 3 14V10Z" stroke="#a33b5c" stroke-width="1.5" />
                              <path d="M12 6L12 8M12 8L12 10M12 8H9.99998M12 8L14 8" stroke="#a33b5c" stroke-width="1.5" stroke-linecap="round" />
                              <path d="M8 14H16" stroke="#a33b5c" stroke-width="1.5" stroke-linecap="round" />
                              <path d="M9 18H15" stroke="#a33b5c" stroke-width="1.5" stroke-linecap="round" />
                            </svg>
                          </i>
                          {job.totalRecords || job.progress?.totalRecords || 0}
                        </h4>
                        <p>Total Records</p>
                      </div>
                    </div>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box success-main'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 10C2.24186 10 0 7.75814 0 5C0 2.24186 2.24186 0 5 0C7.75814 0 10 2.24186 10 5C10 7.75814 7.75814 10 5 10ZM5 0.697674C2.62791 0.697674 0.697674 2.62791 0.697674 5C0.697674 7.37209 2.62791 9.30233 5 9.30233C7.37209 9.30233 9.30233 7.37209 9.30233 5C9.30233 2.62791 7.37209 0.697674 5 0.697674Z" fill="#16A34A"></path><path d="M4.23915 6.76841C4.14613 6.76841 4.05775 6.7312 3.99264 6.66609L2.67636 5.34981C2.54147 5.21492 2.54147 4.99167 2.67636 4.85679C2.81124 4.7219 3.0345 4.7219 3.16938 4.85679L4.23915 5.92655L6.62985 3.53586C6.76473 3.40097 6.98799 3.40097 7.12287 3.53586C7.25775 3.67074 7.25775 3.89399 7.12287 4.02888L4.48566 6.66609C4.42054 6.7312 4.33217 6.76841 4.23915 6.76841Z" fill="#16A34A"></path></svg>
                          </i>
                          {detailedJobData?.results?.created || job.recordsProcessed || 0}
                        </h4>
                        <p>Created</p>
                      </div>
                    </div>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box error-main'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10px" height="10px" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                              <g fill-rule="evenodd">
                                <path d="M0 7a7 7 0 1 1 14 0A7 7 0 0 1 0 7z" fill="#EF4D56" />
                                <path d="M13 7A6 6 0 1 0 1 7a6 6 0 0 0 12 0z" fill="#FFF" />
                                <path d="M7 5.969L5.599 4.568a.29.29 0 0 0-.413.004l-.614.614a.294.294 0 0 0-.004.413L5.968 7l-1.4 1.401a.29.29 0 0 0 .004.413l.614.614c.113.114.3.117.413.004L7 8.032l1.401 1.4a.29.29 0 0 0 .413-.004l.614-.614a.294.294 0 0 0 .004-.413L8.032 7l1.4-1.401a.29.29 0 0 0-.004-.413l-.614-.614a.294.294 0 0 0-.413-.004L7 5.968z" fill="#EF4D56" />
                              </g>
                            </svg>
                          </i>
                          {detailedJobData?.results?.failedTotal || job.failedRecords || 0}
                        </h4>
                        <p>Failed</p>
                      </div>
                    </div>
                    <div className='col-lg-3 col-md-6 col-sm-12'>
                      <div className='summary-box warning-main'>
                        <h4 color="">
                          <i className='icon'>
                            <svg width="10px" height="10px" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M7 14A7 7 0 1 1 7 0a7 7 0 0 1 0 14z" fill='#EA580C' /><path d="M7 13A6 6 0 1 0 7 1a6 6 0 0 0 0 12z" fill="#FFF" fill-rule="nonzero" /><path d="M6.415 7.04L4.579 5.203a.295.295 0 0 1 .004-.416l.349-.349a.29.29 0 0 1 .416-.004l2.214 2.214a.289.289 0 0 1 .019.021l.132.133c.11.11.108.291 0 .398L5.341 9.573a.282.282 0 0 1-.398 0l-.331-.331a.285.285 0 0 1 0-.399L6.415 7.04zm2.54 0L7.119 5.203a.295.295 0 0 1 .004-.416l.349-.349a.29.29 0 0 1 .416-.004l2.214 2.214a.289.289 0 0 1 .019.021l.132.133c.11.11.108.291 0 .398L7.881 9.573a.282.282 0 0 1-.398 0l-.331-.331a.285.285 0 0 1 0-.399L8.955 7.04z" fill='#EA580C' /></svg>
                          </i>
                          {detailedJobData?.results?.duplicatesTotal || job.skippedRecords || 0}
                        </h4>
                        <p>Duplicates</p>
                      </div>
                    </div>
                  </>
                )}

                {/* Export Download Section */}
                {detailedJobData?.transferType === 'export' && detailedJobData?.results?.downloadAvailable && (
                  <div className='col-12 mt-4'>
                    <div className='summary-box' style={{ background: 'var(--sa-dark-surface-light)', border: '1px solid #3b82f6' }}>
                      <h4>Export Ready for Download</h4>
                      <p>Your export file is ready. Click below to download.</p>
                      <Button
                        className='btn-style-new'
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          if (detailedJobData?.results?.downloadUrl) {
                            window.open(detailedJobData.results.downloadUrl, '_blank');
                          } else {
                            toast.error('Download URL not available');
                          }
                        }}
                      >
                        Download Export File
                      </Button>
                    </div>
                  </div>
                )}

                {/* Detailed Results Accordion - Only for imports */}
                {detailedJobData && job.status === 'completed' && detailedJobData?.transferType !== 'export' && (
                  <div className='col-12 mt-4'>
                    <h4>Detailed Results</h4>
                    <Accordion allowMultipleExpanded allowZeroExpanded>
                      {/* Failed Records */}
                      {detailedJobData.results.failedTotal > 0 && (
                        <AccordionItem>
                          <AccordionItemHeading>
                            <AccordionItemButton>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography>
                                  Failed Records ({detailedJobData.results.failedTotal})
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button
                                  size="small"
                                  className='btn-style-border import-btn'
                                  startIcon={<DownloadIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadFailedRecords();
                                  }}
                                >
                                  Download
                                </Button>
                              </Box>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel>
                            <div className="failed-records-table">
                              <Typography variant="caption" className='mb-2' color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                Showing first {detailedJobData.results.failed.length} of {detailedJobData.results.failedTotal} failed records
                              </Typography>
                              {detailedJobData.results.failed.map((record, index) => (
                                <Card key={index} sx={{ mb: 1 }} clas>
                                  <CardContent sx={{ py: 1 }}>
                                    <Typography variant="caption" color="error">
                                      Row {record.row}: {record.errorMessages.join(', ')}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                      Data: {JSON.stringify(record.data, null, 2)}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </AccordionItemPanel>
                        </AccordionItem>
                      )}

                      {/* Duplicate Records */}
                      {detailedJobData.results.duplicatesTotal > 0 && (
                        <AccordionItem>
                          <AccordionItemHeading>
                            <AccordionItemButton>
                              <Typography>
                                Duplicate Records ({detailedJobData.results.duplicatesTotal})
                              </Typography>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel>
                            <div className="duplicate-records-table failed-records-table">
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                Showing first {detailedJobData.results.duplicates.length} of {detailedJobData.results.duplicatesTotal} duplicate records
                              </Typography>
                              {detailedJobData.results.duplicates.map((record, index) => (
                                <Card key={index} sx={{ mb: 1 }}>
                                  <CardContent sx={{ py: 1 }}>
                                    <Typography variant="caption" color="warning.main">
                                      Row {record.row} - Existing Record ID: {record.existingRecordId}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                      Data: {JSON.stringify(record.data, null, 2)}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </AccordionItemPanel>
                        </AccordionItem>
                      )}

                      {/* Updated Records */}
                      {detailedJobData.results.updated > 0 && (
                        <AccordionItem>
                          <AccordionItemHeading>
                            <AccordionItemButton>
                              <Typography>
                                Updated Records ({detailedJobData.results.updated})
                              </Typography>
                            </AccordionItemButton>
                          </AccordionItemHeading>
                          <AccordionItemPanel>
                            <div className="updated-records-table failed-records-table">
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                Showing first {detailedJobData.results.updatedSample.length} of {detailedJobData.results.updated} updated records
                              </Typography>
                              {detailedJobData.results.updatedSample.map((recordId, index) => (
                                <Card key={index} sx={{ mb: 1 }}>
                                  <CardContent sx={{ py: 1 }}>
                                    <Typography variant="caption" color="success.main">
                                      Updated Record ID: {recordId}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          </AccordionItemPanel>
                        </AccordionItem>
                      )}
                    </Accordion>
                  </div>
                )}

                {job.errorLogs && job.errorLogs.length > 0 && (
                  <>
                    <div className='col-12'>
                      <h4>Error Logs</h4>
                      <div className='summary-box'>
                        {job.errorLogs.map((error, index) => (
                          <p key={index}>Row {error.row}: {error.message}</p>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {/* Configuration Section */}
                {(detailedJobData?.config || job?.config) && (
                  <>
                    <div className='col-12 mt-4'>
                      <h4>{detailedJobData?.transferType === 'export' ? 'Export' : 'Import'} Configuration</h4>
                    </div>
                    
                    {detailedJobData?.transferType === 'export' ? (
                      <>
                        {detailedJobData?.config?.selectedFields && (
                          <div className='col-12'>
                            <div className='summary-box'>
                              <p>Exported Fields:</p>
                              <h4 color="warning.main">{detailedJobData.config.selectedFields.length} fields selected</h4>
                              <Typography variant="caption" color="text.secondary">
                                {detailedJobData.config.selectedFields.join(', ')}
                              </Typography>
                            </div>
                          </div>
                        )}
                        {detailedJobData?.metrics && (
                          <>
                            <div className='col-lg-4 col-md-6 col-sm-12'>
                              <div className='summary-box'>
                                <p>Export Duration:</p>
                                <h4 color="warning.main">
                                  {detailedJobData.metrics.totalDuration 
                                    ? `${(detailedJobData.metrics.totalDuration / 1000).toFixed(2)}s` 
                                    : 'N/A'}
                                </h4>
                              </div>
                            </div>
                            <div className='col-lg-4 col-md-6 col-sm-12'>
                              <div className='summary-box'>
                                <p>Date Format:</p>
                                <h4 color="warning.main">{detailedJobData.config?.dateFormat || 'YYYY-MM-DD'}</h4>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div className='col-lg-4 col-md-6 col-sm-12'>
                          <div className='summary-box'>
                            <p>Duplicate Handling: </p>
                            <h4 color="warning.main">{detailedJobData?.config?.['duplicateHandling'] || job?.config?.['duplicateHandling'] || 'skip'}</h4>
                          </div>
                        </div>
                        <div className='col-lg-4 col-md-6 col-sm-12'>
                          <div className='summary-box'>
                            <p>Batch Size:</p>
                            <h4 color="warning.main"> {detailedJobData?.config?.['batchSize'] || job?.config?.['batchSize'] || 100}</h4>
                          </div>
                        </div>
                        {(detailedJobData?.config?.['duplicateCheckFields'] || job?.config?.['duplicateCheckFields']) && job?.config?.['duplicateCheckFields'].length > 0 && (
                          <div className='col-lg-4 col-md-6 col-sm-12'>
                            <div className='summary-box'>
                              <p>Duplicate Check Fields:</p>
                              <h4 color="warning.main"> {(detailedJobData?.config?.['duplicateCheckFields'] || job?.config?.['duplicateCheckFields']).join(', ')}</h4>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            <div className='footer-modal-area'>
              <button className='btn-style-border' onClick={() => { setDetailsDialog({ open: false, job: null }); setDetailedJobData(null); }}>Close</button>
              {job['canRollback'] &&
                <button className='btn-style-new' onClick={() => handleRollback(job)} >
                  <RollbackIcon /> Rollback Import
                </button>
              }
            </div>
          </div>
        </ModalBody>
      </Modal>
    )
  }

  const isPaginated = filteredHistory.length > 100;
  const headerScrollHeight = isPaginated ? 'calc(100vh - 189px)' : 'calc(100vh - 161px)';
  const responsiveClass = isPaginated ? 'datatable--paginated' : 'datatable--fullheight property-table';

  return (
    <Box>
      {!importWizardOpen && <>
        <div className='title-top-block'>
          <div className='left-area'>
            <h2>Imports & Exports</h2>
          </div>
        </div>
        <div className='settings-card'>
          <div className='header-table-crm'>
            <h3>Data Transfer History</h3>
            <button variant="contained" className='btn-style-new' onClick={() => setImportWizardOpen(true)}>
              Import Records
            </button>
          </div>
        </div>

        {/* Import History Table */}
        <div className="settings-card" style={{ padding: 0, overflow: 'hidden', marginTop: 0 }}>
        <div className="table-container filters-table mt-3 property-tables-area">
          {selectedRows.length === 1 ? (
            <div className='header-table-filters advanced-table-filters style-filter-show'>
              <div className='left-area'>
                <p>1 contact selected</p>
                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.00049 5L11.0672 2.93327L9.00049 0.866576" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M1.06689 2.93359L11.0671 2.93359" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Assign
                </button>
                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.14459 1.64981L2.38167 5.63273C2.23959 5.78398 2.10209 6.0819 2.07459 6.28815L1.90501 7.77315C1.84542 8.3094 2.23042 8.67606 2.76209 8.5844L4.23792 8.33232C4.44417 8.29565 4.73292 8.1444 4.87501 7.98856L8.63792 4.00565C9.28876 3.31815 9.58209 2.5344 8.56917 1.57648C7.56084 0.627731 6.79542 0.962314 6.14459 1.64981Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M5.5166 2.31445C5.71368 3.57945 6.74035 4.54654 8.01452 4.67487" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M1.44189 10.0835H9.69189" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Edit
                </button>
                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.69189 2.74089C8.16564 2.58964 6.63023 2.51172 5.09939 2.51172C4.19189 2.51172 3.28439 2.55755 2.37689 2.64922L1.44189 2.74089" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3.96289 2.27775L4.06372 1.67734C4.13706 1.24192 4.19206 0.916504 4.96664 0.916504H6.16747C6.94206 0.916504 7.00164 1.26025 7.07039 1.68192L7.17122 2.27775" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M8.70641 4.18945L8.4085 8.80487C8.35808 9.52445 8.31683 10.0836 7.03808 10.0836H4.09558C2.81683 10.0836 2.77558 9.52445 2.72516 8.80487L2.42725 4.18945" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.80176 7.5625H6.32801" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.4209 5.729H6.71257" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Delete
                </button>

                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.06689 5.5H10.0669" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M5.56689 10V1" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Create tasks
                </button>

                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_1030_1905)">
                        <path d="M4.65007 2.75H7.40007M4.65007 6.41667H7.40007M4.65007 4.58333H10.1501M4.65007 8.25H10.1501M1.44173 4.58333H2.3584C2.61153 4.58333 2.81673 4.37813 2.81673 4.125V3.20833C2.81673 2.9552 2.61153 2.75 2.3584 2.75H1.44173C1.1886 2.75 0.983398 2.9552 0.983398 3.20833V4.125C0.983398 4.37813 1.1886 4.58333 1.44173 4.58333ZM1.44173 8.25H2.3584C2.61153 8.25 2.81673 8.0448 2.81673 7.79167V6.875C2.81673 6.62187 2.61153 6.41667 2.3584 6.41667H1.44173C1.1886 6.41667 0.983398 6.62187 0.983398 6.875V7.79167C0.983398 8.0448 1.1886 8.25 1.44173 8.25Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-linecap="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_1030_1905">
                          <rect width="11" height="11" fill="white" transform="translate(0.0668945)" />
                        </clipPath>
                      </defs>
                    </svg>
                  </i>
                  Add to static list
                </button>
              </div>
              <div className='right-side-filter'>
                <button className='close' onClick={() => { setToggleCleared(prev => !prev); setSelectedRows([]); }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <mask id="path-1-inside-1_1030_1913" fill="white">
                      <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" />
                    </mask>
                    <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                    <path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)" />
                  </svg>
                </button>
              </div>
            </div>
          ) : selectedRows.length > 1 ? (
            <div className='header-table-filters advanced-table-filters style-filter-show'>
              <div className='left-area'>
                <p>20 contact selected</p>
                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.00049 5L11.0672 2.93327L9.00049 0.866576" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M1.06689 2.93359L11.0671 2.93359" stroke="var(--primary-color)" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Assign
                </button>
                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.14459 1.64981L2.38167 5.63273C2.23959 5.78398 2.10209 6.0819 2.07459 6.28815L1.90501 7.77315C1.84542 8.3094 2.23042 8.67606 2.76209 8.5844L4.23792 8.33232C4.44417 8.29565 4.73292 8.1444 4.87501 7.98856L8.63792 4.00565C9.28876 3.31815 9.58209 2.5344 8.56917 1.57648C7.56084 0.627731 6.79542 0.962314 6.14459 1.64981Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M5.5166 2.31445C5.71368 3.57945 6.74035 4.54654 8.01452 4.67487" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M1.44189 10.0835H9.69189" stroke="var(--primary-color)" stroke-width="0.6875" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Edit
                </button>
                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.69189 2.74089C8.16564 2.58964 6.63023 2.51172 5.09939 2.51172C4.19189 2.51172 3.28439 2.55755 2.37689 2.64922L1.44189 2.74089" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M3.96289 2.27775L4.06372 1.67734C4.13706 1.24192 4.19206 0.916504 4.96664 0.916504H6.16747C6.94206 0.916504 7.00164 1.26025 7.07039 1.68192L7.17122 2.27775" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M8.70641 4.18945L8.4085 8.80487C8.35808 9.52445 8.31683 10.0836 7.03808 10.0836H4.09558C2.81683 10.0836 2.77558 9.52445 2.72516 8.80487L2.42725 4.18945" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.80176 7.5625H6.32801" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M4.4209 5.729H6.71257" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Delete
                </button>

                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.06689 5.5H10.0669" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M5.56689 10V1" stroke="var(--primary-color)" stroke-width="1.125" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </i>
                  Create tasks
                </button>

                <button className='assing-btn'>
                  <i className='icon'>
                    <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clip-path="url(#clip0_1030_1905)">
                        <path d="M4.65007 2.75H7.40007M4.65007 6.41667H7.40007M4.65007 4.58333H10.1501M4.65007 8.25H10.1501M1.44173 4.58333H2.3584C2.61153 4.58333 2.81673 4.37813 2.81673 4.125V3.20833C2.81673 2.9552 2.61153 2.75 2.3584 2.75H1.44173C1.1886 2.75 0.983398 2.9552 0.983398 3.20833V4.125C0.983398 4.37813 1.1886 4.58333 1.44173 4.58333ZM1.44173 8.25H2.3584C2.61153 8.25 2.81673 8.0448 2.81673 7.79167V6.875C2.81673 6.62187 2.61153 6.41667 2.3584 6.41667H1.44173C1.1886 6.41667 0.983398 6.62187 0.983398 6.875V7.79167C0.983398 8.0448 1.1886 8.25 1.44173 8.25Z" stroke="var(--primary-color)" stroke-width="0.6875" stroke-linecap="round" />
                      </g>
                      <defs>
                        <clipPath id="clip0_1030_1905">
                          <rect width="11" height="11" fill="white" transform="translate(0.0668945)" />
                        </clipPath>
                      </defs>
                    </svg>
                  </i>
                  Add to static list
                </button>
              </div>
              <div className='right-side-filter'>
                <button className='close' onClick={() => { setToggleCleared(prev => !prev); setSelectedRows([]); }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <mask id="path-1-inside-1_1030_1913" fill="white">
                      <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" />
                    </mask>
                    <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                    <path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div className='header-table-filters advanced-table-filters'>
              <div className='left-area'>
                <div className='searchbox'>
                  {/* <TextField fullWidth hiddenLabel size="small" name='search' variant="standard" placeholder='Search name, phone, email, Company' /> */}
                  <TextField
                    fullWidth
                    size="small"
                    value={searchTerm}
                    placeholder="Search by file name..."
                    onChange={(e) => setSearchTerm(e['target']['value'])}
                  />
                  <i className='icon'>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                      <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                    </svg>
                  </i>
                </div>
                <div className='filter-box'>
                  <Select
                    value={selectedModule}
                    onChange={(e) => setSelectedModule(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) return 'Module';
                      const selectedItem = modules.find(objectType => objectType.value === selected);
                      return selectedItem ? selectedItem.label : 'Module';
                    }}
                    withoutlable
                  >
                    {modules.map(objectType => (
                      <MenuItem key={objectType.value} value={objectType.value}>
                        {objectType.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className='filter-box'>
                  <Select
                    value={selectedTransferType}
                    onChange={(e) => setSelectedTransferType(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) return 'Transfer Type';
                      const selectedItem = transferTypes.find(type => type.value === selected);
                      return selectedItem ? selectedItem.label : 'Transfer Type';
                    }}
                    withoutlable
                  >
                    {transferTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className='filter-box'>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) return 'Status';
                      const selectedItem = statuses.find(status => status.value === selected);
                      return selectedItem ? selectedItem.label : 'Status';
                    }}
                    withoutlable
                  >
                    {statuses.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>

                </div>
              </div>
            </div>
          )}

          <style>{checkboxStyles}</style>
          <DataTable
            columns={columns}
            data={filteredHistory}
            fixedHeader
            keyField="_id"
            selectableRows
            highlightOnHover
            persistTableHead
            pagination
            progressPending={isLoading}
            paginationPerPage={rowsPerPage}
            fixedHeaderScrollHeight={headerScrollHeight}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            className={`table-salesai ${responsiveClass}`}
            paginationResetDefaultPage={resetPaginationToggle}
            progressComponent={<TableAnimationFiles />}
            noDataComponent={<div className='notfound-area'>
              <div className='img-box'>
                <img alt="..." src={require('../../../static/img/not-found.png')} />
              </div>
              <p>No transfers found</p>
            </div>}
            sortIcon={<CustomSortIcon />}
          />
        </div>
        </div>
        {renderDetailsDialog()}
      </>}

      {/* Import Wizard Modal */}
      {importWizardOpen && (
        <ImportWizard
          importWizardOpen={importWizardOpen}
          setImportWizardOpen={setImportWizardOpen}
          onComplete={() => { fetchImportHistory() }}
          objectType={selectedModule === 'all' ? 'contacts' : selectedModule}
        />
      )}
    </Box>
  );
};

const ResizableHeader = ({ title, width, onResize }) => {
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    startX.current = e.clientX;
    startWidth.current = width;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    const delta = e.clientX - startX.current;
    const newWidth = startWidth.current + delta;
    if (newWidth >= 60) {
      onResize(newWidth);
    }
  };

  const onMouseUp = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  return (
    <div style={{ position: 'relative', height: '100%', padding: '0 10px' }}>
      <div style={{ padding: '6px 0' }}>{title}</div>
      <div
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          height: '100%',
          width: '5px',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
        }}
      />
    </div>
  );
};

const checkboxStyles = `
  .table-salesai input[type="checkbox"] {
    width: 13px;
    height: 13px;
    cursor: pointer;
    accent-color: #cbd6e2;
    transform: scale(1);
    opacity: 0.45;
  }

  .table-salesai input[type="checkbox"]:checked {
    opacity: 1;
    accent-color: var(--primary-color);
  }
`;

export default ImportHistory;